import { useCallback, useContext, useState, useMemo } from "react";
import { AuthContext } from "../contexts/user-context/user-context";
import { API_ENDPOINT } from "../configurations/global.config";
import axios from "axios";
import { useSnackBar } from "../contexts/snackbar";
import { IInternalTransferTask } from "../interfaces/delivery-company/tasks";
import { BranchType } from "../interfaces/delivery-company/branches";
import { DriverType } from "../interfaces/delivery-company/drivers";
import { Money } from "../utils/money";
import { PaginatedResponse, usePaginatedData } from "./pagination";
import { InternalTransferTaskState } from "../enums/tasks";
import { useSelectedBranch } from "../contexts/selected-branch-context";
import { getConfigWithBranch } from "../utils/branch-api-config";

interface UseInternalTransferTasksOptions {
  autoFetch?: boolean;
  sourceBranchId?: string | number | null;
  targetBranchId?: string | number | null;
  filterActive?: boolean | null; // true for active, false for past, null for both
  driverId?: string | number | null; // Filter by driver ID
}

export interface CreateInternalTransferTaskInput {
  target_branch: BranchType;
  orders?: string[]; // order_ids
  driver?: DriverType | null;
  driver_commission?: Money[];
}

export interface UpdateInternalTransferTaskInput {
  source_branch?: BranchType;
  target_branch?: BranchType;
  orders?: string[]; // order_ids
  driver?: DriverType | null;
  driver_commission?: Money[];
  status?: string;
}

export const useInternalTransferTasks = ({
  autoFetch = true,
  sourceBranchId,
  targetBranchId,
  filterActive,
  driverId,
}: UseInternalTransferTasksOptions = {}) => {
  const { config: authConfig, user } = useContext(AuthContext);
  const { selectedBranch } = useSelectedBranch();
  const { showMessage } = useSnackBar();

  // Get config with branch context - memoize to prevent infinite loops
  const config = useMemo(() => getConfigWithBranch(authConfig, selectedBranch), [authConfig, selectedBranch]);

  // Track refresh trigger to force refetch even when on same page
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Track which task is being updated
  const [updatingTaskId, setUpdatingTaskId] = useState<string | number | null>(null);

  // Use paginated data hook for fetching tasks
  const {
    data: tasks,
    count,
    loading: isLoading,
    refetch: fetchTasks,
    prependRecord,
    updateRecord
  } = usePaginatedData<IInternalTransferTask>(
    async (page, pageSize) => {
      if (!user) {
        return {
          count: 0,
          results: [] as IInternalTransferTask[],
        } as PaginatedResponse<IInternalTransferTask>;
      }

      let endpoint = `${API_ENDPOINT}/v1/internal-transfer-tasks/`;
      const params: string[] = [];
      
      // Add pagination params
      params.push(`page=${page + 1}`);
      params.push(`page_size=${pageSize}`);
      
      if (sourceBranchId) {
        params.push(`source_branch=${sourceBranchId}`);
      }
      
      if (targetBranchId) {
        params.push(`target_branch=${targetBranchId}`);
      }
      
      // Add filter for active/past tasks
      if (filterActive !== null && filterActive !== undefined) {
        if (filterActive) {
          // Active: exclude terminal states (COMPLETED, CANCELLED, FAILED)
          params.push(`status__nin=${InternalTransferTaskState.COMPLETED},${InternalTransferTaskState.CANCELLED},${InternalTransferTaskState.FAILED}`);
        } else {
          // Past: only terminal states
          params.push(`status__in=${InternalTransferTaskState.COMPLETED},${InternalTransferTaskState.CANCELLED},${InternalTransferTaskState.FAILED}`);
        }
      }
      
      // Add filter for driver
      if (driverId) {
        params.push(`driver=${driverId}`);
      }
      
      if (params.length > 0) {
        endpoint += `?${params.join("&")}`;
      }
      
      const response = await axios.get<PaginatedResponse<IInternalTransferTask>>(endpoint, config);
      
      // Transform response from {count, data} to {count, results} format expected by usePaginatedData
      return {
        count: response.data.count,
        results: response.data.results,
        next: null, // Backend should provide this if paginated
        previous: null, // Backend should provide this if paginated
      } as PaginatedResponse<IInternalTransferTask>;
    },
    [sourceBranchId, targetBranchId, filterActive, driverId, user, refreshTrigger, selectedBranch?.id]
  );

  // Get a single task
  const getTask = useCallback(
    async (id: string | number): Promise<IInternalTransferTask | null> => {
      try {
        const response = await axios.get<IInternalTransferTask>(
          `${API_ENDPOINT}/v1/internal-transfer-task/${id}/`,
          config
        );
        return response.data;
      } catch (error) {
        console.error("Failed to fetch internal transfer task", error);
        showMessage("Failed to fetch internal transfer task", "error");
        return null;
      }
    },
    [config, showMessage]
  );

  // Create a task
  const createTask = useCallback(
    async (taskData: CreateInternalTransferTaskInput): Promise<IInternalTransferTask | null> => {
      try {
        const payload: any = {
          ...taskData,
          // Send branches as objects, not IDs
          target_branch: taskData.target_branch,
          driver: taskData.driver ? (typeof taskData.driver === 'object' ? taskData.driver.id : taskData.driver) : null,
        };
        
        const response = await axios.post<IInternalTransferTask>(
          `${API_ENDPOINT}/v1/internal-transfer-tasks/`,
          payload,
          config
        );
        const newTask = response.data;
        prependRecord(newTask)
        showMessage("Internal transfer task created successfully", "success");
        return newTask;
      } catch (error: any) {
        console.error("Failed to create internal transfer task", error);
        const errorMessage = error.response?.data?.error || error.response?.data?.message || "Failed to create internal transfer task";
        showMessage(errorMessage, "error");
        return null;
      }
    },
    [config, showMessage, fetchTasks]
  );

  // Update a task
  const updateTask = useCallback(
    async (id: string | number, updates: UpdateInternalTransferTaskInput): Promise<IInternalTransferTask | null> => {
      setUpdatingTaskId(id);
      try {
        const payload: any = { ...updates };
        
        // Send branches as objects, not IDs
        if (updates.source_branch !== undefined) {
          payload.source_branch = typeof updates.source_branch === 'object' ? updates.source_branch : updates.source_branch;
        }
        if (updates.target_branch !== undefined) {
          payload.target_branch = typeof updates.target_branch === 'object' ? updates.target_branch : updates.target_branch;
        }
        if (updates.driver !== undefined) {
          payload.driver = updates.driver;
        }
        
        const response = await axios.patch<IInternalTransferTask>(
          `${API_ENDPOINT}/v1/internal-transfer-task/${id}/`,
          payload,
          config
        );
        const updatedTask = response.data;
        showMessage("Internal transfer task updated successfully", "success");
        updateRecord(updatedTask)
        return updatedTask;
      } catch (error: any) {
        console.error("Failed to update internal transfer task", error);
        const errorMessage = error.response?.data?.error || error.response?.data?.message || "Failed to update internal transfer task";
        showMessage(errorMessage, "error");
        return null;
      } finally {
        setUpdatingTaskId(null);
      }
    },
    [config, showMessage, updateRecord]
  );

  // Delete a task
  const deleteTask = useCallback(
    async (id: string | number): Promise<boolean> => {
      try {
        await axios.delete(`${API_ENDPOINT}/v1/internal-transfer-task/${id}/`, config);
        showMessage("Internal transfer task deleted successfully", "success");
        updateRecord({...tasks.find(t => t.id == id)!, status: InternalTransferTaskState.CANCELLED});
        return true;
      } catch (error: any) {
        console.error("Failed to delete internal transfer task", error);
        const errorMessage = error.response?.data?.error || error.response?.data?.message || "Failed to delete internal transfer task";
        showMessage(errorMessage, "error");
        return false;
      }
    },
    [config, showMessage, fetchTasks]
  );

  return {
    tasks,
    count,
    fetchTasks,
    getTask,
    createTask,
    updateTask,
    deleteTask,
    isLoading,
    updatingTaskId,
  };
};

export default useInternalTransferTasks;

